import { useEffect, useRef, useState } from 'react';

function noise2d(x: number, y: number): number {
  const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

function smoothNoise(x: number, y: number): number {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = x - ix, fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const a = noise2d(ix, iy), b = noise2d(ix + 1, iy);
  const c = noise2d(ix, iy + 1), d = noise2d(ix + 1, iy + 1);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

function fbm(x: number, y: number): number {
  let v = 0, amp = 0.5, freq = 1;
  for (let i = 0; i < 4; i++) {
    v += amp * smoothNoise(x * freq, y * freq);
    amp *= 0.5; freq *= 2;
  }
  return v;
}

function draw(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const w = canvas.parentElement!.clientWidth;
  const h = canvas.parentElement!.scrollHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  ctx.scale(dpr, dpr);

  const style = getComputedStyle(document.documentElement);
  const fg = style.getPropertyValue('--charcoal').trim() || '#1c1c1c';
  ctx.fillStyle = fg;

  const isMobile = w < 640;
  const contentWidth = Math.min(w, 672);
  const textStart = (w - contentWidth) / 2 + 24;
  const margin = Math.max(4, textStart - 8);
  const lineSpacing = isMobile ? 3 : 3.5;
  const maxExtra = isMobile ? 12 : 40;
  const baseSteps = isMobile ? 12 : 30;
  const lineCount = Math.floor(h / lineSpacing);
  const stepLen = 4;

  for (let side = 0; side < 2; side++) {
    for (let i = 0; i < lineCount; i++) {
      let x = side === 0 ? 0 : w;
      let y = (h / lineCount) * i + (Math.random() - 0.5) * 6;

      const maxSteps = baseSteps + Math.floor(Math.random() * maxExtra);
      for (let s = 0; s < maxSteps; s++) {
        const distFromEdge = Math.min(x, w - x);
        if (distFromEdge > margin) break;

        const angle = fbm(x * 0.006, y * 0.006) * Math.PI * 3;
        const inward = side === 0 ? 0.35 : -0.35;
        x += Math.cos(angle) * stepLen * 0.5 + inward * stepLen;
        y += Math.sin(angle) * stepLen;

        const edgeFactor = 1 - distFromEdge / margin;
        ctx.globalAlpha = edgeFactor * (0.18 + Math.random() * 0.12);
        ctx.beginPath();
        ctx.arc(x, y, 0.6 + edgeFactor * 1.0 + Math.random() * 0.3, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  ctx.globalAlpha = 1;
}

export function MarginFlair() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    draw(canvas);
    const t = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(t);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={ready ? 'margin-flair-in' : undefined}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        pointerEvents: 'none',
        zIndex: 10,
        opacity: 0,
      }}
    />
  );
}
